/**
 * One bar of one voice as the events that write it: heads, lengths and ties.
 *
 * Where {@link piecesOf} leaves off with stretches of time, this turns them into notes and
 * rests. A stretch that no single note value covers is written as several tied together, a
 * bar with nothing in it as the bar's one centred rest, and a drum the kit does not have is
 * left out and said. A note held over a bar line where the key changes keeps the spelling
 * it was struck with, since a tie joins two heads of one name.
 */

import { fillTicks, plain } from "../document/duration";
import { note, rest } from "../document/create";
import { pitchOf, spell } from "../document/pitch";
import type { NotePitch } from "../document/pitch";
import type { MusicEvent } from "../document/types";
import type { PercussionKit } from "../document/percussion";
import type { PlannedBar } from "./plan";
import type { Head, Piece } from "./spans";

/** A head as it is written, with the ties its piece has. */
interface Written {
  readonly pitch: NotePitch;
  readonly head: Head;
}

/** The heads a piece is written with, dropping any drum the kit does not have. */
function headsOf(
  piece: Piece,
  bars: readonly PlannedBar[],
  kit: PercussionKit | null,
  warn: (text: string) => void,
): readonly Written[] {
  const heads: Written[] = [];
  for (const head of piece.heads) {
    if (kit === null) {
      heads.push({ pitch: spell(head.key, bars[head.struck]?.fifths ?? 0), head });
      continue;
    }
    const drum = kit.notes.find((candidate) => candidate.midi === head.key);
    if (drum === undefined) {
      warn(`key ${head.key} is not one of the drums in the ${kit.name}, so it is left out`);
      continue;
    }
    heads.push({ pitch: { ...pitchOf(drum.step, 0, drum.octave), drum: drum.name }, head });
  }
  return heads;
}

/** Whether a voice plays nothing in a bar. */
export function silent(pieces: readonly Piece[]): boolean {
  return pieces.every((piece) => piece.heads.length === 0);
}

/** A bar of nothing: the one centred rest, whatever the metre. */
export function barRest(): readonly MusicEvent[] {
  return [rest(plain("whole"), true)];
}

/** The events of bar `index` of one voice. */
export function eventsOf(
  pieces: readonly Piece[],
  bars: readonly PlannedBar[],
  index: number,
  kit: PercussionKit | null,
  warn: (text: string) => void,
): readonly MusicEvent[] {
  const only = pieces.length === 1 ? pieces[0] : undefined;
  if (only !== undefined && only.heads.length === 0 && only.ticks === bars[index]?.ticks) {
    return barRest();
  }

  const events: MusicEvent[] = [];
  for (const piece of pieces) {
    const heads = headsOf(piece, bars, kit, warn);
    const durations = fillTicks(piece.ticks);
    if (durations === null) {
      warn(`a note lasting ${piece.ticks} ticks cannot be written, so it is left out`);
      continue;
    }
    durations.forEach((duration, nth) => {
      if (heads.length === 0) {
        events.push(rest(duration));
        return;
      }
      const tied = heads.map(({ pitch, head }) => ({
        ...pitch,
        tieStop: head.tiedFrom || nth > 0,
        tieStart: head.tiedTo || nth < durations.length - 1,
      }));
      events.push(note(tied, duration));
    });
  }
  return events;
}
